/* global __RELEASE__ */

// This file contains the boilerplate to execute your React app.
// If you want to modify your application's content, start in "index.js"

import * as Sentry from '@sentry/browser';
import {TrackFocus} from '@homeaway/a11y-tools';
import {SENTRY_URL, ONBOARDING_COMPLETE_KEY} from './src/constants';
import {getEnvironmentName} from 'virtual-tour-viewer/src/utils/UIHelper';
import OverlayActions from './src/data/Overlay/OverlayActions';
import ReactDOM from 'react-dom';
import OverlayContainer from './src/containers/OverlayContainer.react';
import React from 'react';
import TourHavrProvider from './src/Contexts/Tour/HavrProvider.react';
import MUXNavigationProvider from './src/Contexts/Navigation/MUXNavigationProvider.react';
import LogProvider from './src/Contexts/Logging/LogSubjectProvider.react';
import ConsoleLog from './src/logs/ConsoleLog';
import EdapLog from './src/logs/EdapLog';
import NavigationContext from './src/Contexts/Navigation/Context';
import TourContext from './src/Contexts/Tour/Context';
import InitialImageProvider from './src/Contexts/InitialImage/InitialImageProvider.react';
import NavigationDataTransformProvider from './src/Contexts/Navigation/NavigationDataTransformProvider.react';
import AbacusProvider from './src/Contexts/Abacus/Provider.react';
import AbacusContext from './src/Contexts/Abacus/Context';
import AbacusExperiments from './src/utils/AbacusExperiments';
import {getHash} from './src/utils/DomHelper';
import {isTourFullyProcessed} from './src/utils/TourDataUtils';

const queryString = require('query-string');

let cameraHeight = 1.3;
let vectorScale = 0.005;
let sphereRadius = 1000;
/*
Append the following query parameters to overwrite default Camera Height and Scale factor
which are set to support Materport.

TruPlace
&sf=.02&ch=5
Ex: http://localhost:8081/index.html?tour=a7584ccf-4c45-41d5-814b-d9216723cb9b&sf=.02&ch=5

IM
&sf=.7&ch=200
Ex: http://localhost:8081/index.html?tour=987260f7-e30c-4ff8-b689-20caa9c86b9f&&sf=.7&ch=200

MP
&sf=.005&ch=1.3
Ex: http://localhost:8081/index.html?tour=5c82f034-5c46-4d94-95b9-210817871e7d&sf=.005&ch=1.3
Ex: http://localhost:8081/index.html?tour=63dc6c71-0cb8-4939-ad26-5243ce51fb0b&sf=.005&ch=1.3


HAVR
&sf=.5&ch=50
Ex: http://localhost:8081/index.html?tour=HAVR1&sf=.5&ch=50
*/

function getClientParams(qString) {
    let needToAdjustCamera = true;
    let debug;
    let useSampleData;
    // eslint-disable-next-line no-undefined
    if (qString.needToAdjustCamera !== undefined) {
        let test =
            qString.needToAdjustCamera !== null
                ? qString.needToAdjustCamera.toLowerCase().trim()
                : null;
        switch (test) {
            case 'true':
            case 'yes':
            case '1':
                needToAdjustCamera = true;
                break;
            case 'false':
            case 'no':
            case '0':
            case null:
                needToAdjustCamera = false;
                break;
            default:
                needToAdjustCamera = true;
        }
    }
    useSampleData = qString.useSampleData;

    if (qString.sf) vectorScale = parseFloat(qString.sf);

    if (qString.ch || qString.ch === 0) cameraHeight = parseFloat(qString.ch);

    if (qString.sr) sphereRadius = parseFloat(qString.sr);

    if (qString.debug) debug = JSON.parse(qString.debug.toLowerCase());

    const locale = qString.locale || 'en_US';

    let tourId = qString.tour;

    let showTutorial = true;
    try {
        showTutorial = localStorage.getItem(ONBOARDING_COMPLETE_KEY)
            ? false
            : `${qString.show_tutorial}`.toLowerCase() === 'true';
    } catch (err) {
        showTutorial = `${qString.show_tutorial}`.toLowerCase() === 'true';
    }

    // eslint-disable-next-line camelcase
    let {cookie_has, cookie_hav, abov} = qString;

    return {
        tourId,
        vectorScale,
        cameraHeight,
        sphereRadius,
        debug,
        locale,
        needToAdjustCamera,
        useSampleData,
        showTutorial,
        cookie_hav,
        cookie_has,
        abov,
    };
}

function init(bundle, parent, options = {}) {
    Sentry.init({
        environment: getEnvironmentName(),
        dsn: SENTRY_URL,
        release: __RELEASE__,
    });
    OverlayActions.showLoadingOverlay(true);
    const qString = queryString.parse(window.location.search);
    const params = getClientParams(qString);
    const trackFocus = new TrackFocus();
    trackFocus.bindEvents();
    // TODO: Should DirectNavigation and TourData should be loaded as params to the applications?
    // They almost certainly shouldnt be asynchronously loaded by a child component. Maybe
    // let the container's be responsible for fetching them and putting them into state.
    // TODO: Translations should have a context/provider.

    if (params.showTutorial) {
        OverlayActions.showOnboardingGuide(
            params.showTutorial,
            0,
            params.tourId,
            true,
            'TOUR_ENTRY'
        );
    }

    const defaultStartSphereId = getHash();

    const initialProperties = {
        defaultStartSphereId,
        bundle,
        options,
        ...params,
    };

    ReactDOM.render(
        <MUXNavigationProvider tourId={initialProperties.tourId}>
            <TourHavrProvider tourId={initialProperties.tourId}>
                <AbacusProvider
                    abov={params.abov}
                    hasId={params.cookie_has}
                    havId={params.cookie_hav}
                    directNavSource={null}
                >
                    <TourContext.Consumer>
                        {(tourData) =>
                            tourData && (
                                <AbacusContext.Consumer>
                                    {(experiments) => (
                                        <InitialImageProvider
                                            tourData={tourData}
                                            defaultStartSphereId={defaultStartSphereId}
                                            useMediaGateway={
                                                experiments[
                                                    AbacusExperiments
                                                        .HA_VT_EG_MEDIA_GATEWAY_THROTTLE
                                                ].value === 1 && isTourFullyProcessed(tourData)
                                                    ? true
                                                    : false
                                            }
                                        >
                                            <NavigationContext.Consumer>
                                                {(navigationData) => (
                                                    <LogProvider
                                                        observers={[
                                                            new ConsoleLog(false, 3),
                                                            new EdapLog(
                                                                {tourId: initialProperties.tourId},
                                                                navigationData
                                                            ),
                                                        ]}
                                                    >
                                                        <AbacusProvider
                                                            abov={params.abov}
                                                            hasId={params.cookie_has}
                                                            havId={params.cookie_hav}
                                                            directNavSource={
                                                                navigationData
                                                                    ? navigationData.source
                                                                    : null
                                                            }
                                                        >
                                                            <NavigationDataTransformProvider
                                                                tourData={tourData}
                                                                navigationData={navigationData}
                                                            >
                                                                <AbacusContext.Consumer>
                                                                    {(newExperiments) => (
                                                                        <OverlayContainer
                                                                            {...initialProperties}
                                                                            useMediaGateway={
                                                                                newExperiments[
                                                                                    AbacusExperiments
                                                                                        .HA_VT_EG_MEDIA_GATEWAY_THROTTLE
                                                                                ].value === 1 &&
                                                                                isTourFullyProcessed(
                                                                                    tourData
                                                                                )
                                                                                    ? true
                                                                                    : false
                                                                            }
                                                                            hideHotspots={
                                                                                navigationData &&
                                                                                newExperiments[
                                                                                    AbacusExperiments
                                                                                        .VRBO_VT_DIRECT_NAV_ONLY
                                                                                ].value === 1
                                                                                    ? true
                                                                                    : false
                                                                            }
                                                                        />
                                                                    )}
                                                                </AbacusContext.Consumer>
                                                            </NavigationDataTransformProvider>
                                                        </AbacusProvider>
                                                    </LogProvider>
                                                )}
                                            </NavigationContext.Consumer>
                                        </InitialImageProvider>
                                    )}
                                </AbacusContext.Consumer>
                            )
                        }
                    </TourContext.Consumer>
                </AbacusProvider>
            </TourHavrProvider>
        </MUXNavigationProvider>,
        parent
    );
}

window.React360 = {init};
